<script setup lang="ts">
import type { HTMLAttributes } from "vue"
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from '@/components/shadcn'
import { cn } from "@/lib/utils"

export interface JAccordionItem {
  /** 아이템 고유값 */
  value: string
  /** 트리거(제목) 텍스트 */
  title?: string
  /** 내용 텍스트 */
  content?: string
  /** 비활성화 여부 */
  disabled?: boolean
}

// 타입 안정성을 위한 Conditional Props 정의
type SingleAccordionProps = {
  /** Accordion 자체의 클래스 */
  class?: HTMLAttributes["class"]
  /** Accordion 아이템 배열 */
  items: JAccordionItem[]
  /** Accordion 타입 - single: 하나씩만 열림 */
  type?: "single"
  /** collapsible 여부 (type이 single일 때만 적용) */
  collapsible?: boolean
  /** 기본 선택된 값 (single 모드) */
  defaultValue?: string
}

type MultipleAccordionProps = {
  /** Accordion 자체의 클래스 */
  class?: HTMLAttributes["class"]
  /** Accordion 아이템 배열 */
  items: JAccordionItem[]
  /** Accordion 타입 - multiple: 여러 개 열림 */
  type: "multiple"
  /** 기본 선택된 값들 (multiple 모드) */
  defaultValue?: string[]
  /** collapsible 여부 (type이 multiple일 때는 무시됨) */
  collapsible?: never
}

type AccordionProps = SingleAccordionProps | MultipleAccordionProps

const props = withDefaults(defineProps<AccordionProps>(), {
  type: "single",
  collapsible: true,
  items: () => []
})

const emit = defineEmits<{
  valueChange: [value: string | string[] | undefined]
}>()
</script>

<template>
  <Accordion 
    :type="props.type" 
    :collapsible="props.type === 'multiple' ? undefined : props.collapsible"
    :default-value="props.defaultValue"
    :class="cn('w-full', props.class)"
    @update:model-value="(value: string | string[] | undefined) => emit('valueChange', value)"
  >
    <AccordionItem 
      v-for="item in props.items" 
      :key="item.value"
      :value="item.value"
      :disabled="item.disabled"
    >
      <AccordionTrigger>
        <slot name="trigger" :item="item">
          {{ item.title }}
        </slot>
      </AccordionTrigger>
      <AccordionContent>
        <slot name="content" :item="item">
          {{ item.content }}
        </slot>
      </AccordionContent>
    </AccordionItem>
  </Accordion>
</template>

